import React from 'react'
import { fetchSponsorEvents } from '@/api/sponsorApi'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { InfiniteSelect, type SelectOption } from '../InfiniteSelect'
import {
  API_SPONSORSHIP_STATUS,
  type SponsorEventListType,
} from '@/schemas/sponsor.schems'

export interface SponsorShipEventSelectValue {
  id: string
  name: string
}

interface SponsorshipSelectProps {
  value?: SponsorShipEventSelectValue | SponsorShipEventSelectValue[] | null
  onValueChange: (
    event: SponsorShipEventSelectValue | SponsorShipEventSelectValue[] | null
  ) => void
  placeholder?: string
  className?: string
  disabled?: boolean
  multiple?: boolean
  maxSelections?: number
  variant?: 'default' | 'compact'
  enableSearch?: boolean
  error?: string
  required?: boolean
  label?: string
}

export function SponsorshipSelect({
  value,
  onValueChange,
  placeholder = 'Select event',
  className = 'w-40 h-9 text-sm font-medium',
  disabled = false,
  multiple = false,
  label = 'Event',
  required = false,
  error,
  maxSelections,
  variant = 'default',
  enableSearch = true,
}: Readonly<SponsorshipSelectProps>) {
  const transformEvent = (sponsorShip: SponsorEventListType): SelectOption => {
    return {
      id: sponsorShip?.event?.id,
      label: sponsorShip?.event.name,
      value: sponsorShip?.event.id,
      eventData: sponsorShip?.event,
    }
  }

  const transformValueToOption = (
    event: SponsorShipEventSelectValue
  ): SelectOption => {
    return {
      id: event.id,
      label: event.name,
      value: event.id,
      eventData: event,
    }
  }

  const selectedOptions = React.useMemo(() => {
    if (!value) return multiple ? [] : null

    if (multiple) {
      return Array.isArray(value) ? value.map(transformValueToOption) : []
    } else {
      return !Array.isArray(value) ? transformValueToOption(value) : null
    }
  }, [value, multiple])

  const handleValueChange = (options: SelectOption | SelectOption[] | null) => {
    if (!options) {
      onValueChange(null)
      return
    }

    if (multiple && Array.isArray(options)) {
      const events: SponsorShipEventSelectValue[] = options
        .filter(option => option.eventData)
        .map(option => option.eventData as SponsorShipEventSelectValue)

      onValueChange(events.length > 0 ? events : null)
    } else if (!multiple && !Array.isArray(options)) {
      const option = options

      if (option.eventData && typeof option.eventData === 'object') {
        onValueChange(option.eventData as SponsorShipEventSelectValue)
      } else {
        const reconstructedEvent: SponsorShipEventSelectValue = {
          id: option.id,
          name: option.label,
        }
        onValueChange(reconstructedEvent)
      }
    }
  }

  const resolvedPlaceholder =
    multiple && placeholder === 'Select event...'
      ? 'Select events...'
      : placeholder

  const compactClassName =
    variant === 'compact'
      ? 'w-56 h-9 border-ss-neutral-200 bg-transparent hover:border-ss-blue-700 focus:border-ss-blue-700'
      : className

  return (
    <InfiniteSelect<SponsorEventListType>
      queryKey={[...SPONSOR_QUERY_KEYS.allEvents]}
      queryFn={fetchSponsorEvents}
      transformData={transformEvent}
      value={selectedOptions}
      onValueChange={handleValueChange}
      placeholder={resolvedPlaceholder}
      label={variant === 'compact' ? undefined : label}
      required={required}
      className={compactClassName}
      error={error}
      disabled={disabled}
      searchPlaceholder="Search events..."
      emptyMessage="No events available"
      noResultsMessage="No events found"
      pageSize={20}
      enableSearch={enableSearch}
      showAvatar={false}
      multiple={multiple}
      maxSelections={maxSelections}
      filterValues={val => val.status === API_SPONSORSHIP_STATUS.ACTIVE}
    />
  )
}

export default SponsorshipSelect
